import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { AuthConfigService } from './auth-config.service';
import { JwtAuthResponse } from '../model/auth.interfaces';
import { TokenStorageService } from './token-storage.service';
import {
  BehaviorSubject,
  catchError,
  filter,
  interval,
  Observable,
  retry,
  Subscription,
  switchMap,
  tap,
  throwError,
  timer,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService implements OnDestroy {
  private readonly httpBackend = inject(HttpBackend);
  private readonly http = new HttpClient(this.httpBackend);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly authConfig = inject(AuthConfigService);

  private refreshSubscription: Subscription | null = null;
  private readonly refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<
    string | null
  >(null);
  private isRefreshing = false;
  private readonly MAX_RETRY_ATTEMPTS = 3;

  get onTokenRefreshed(): Observable<string | null> {
    return this.refreshTokenSubject.asObservable();
  }

  get refreshingToken(): boolean {
    return this.isRefreshing;
  }

  startAutoRefresh(): void {
    this.stopAutoRefresh();

    const config = this.authConfig.getConfig();
    const refreshInterval = config.tokenRefreshInterval || 3600000;

    this.refreshSubscription = interval(refreshInterval)
      .pipe(
        filter(() => !!this.tokenStorage.getRefreshToken()),
        switchMap(() => this.refreshToken()),
        catchError((error) => {
          console.error('Error al renovar el token:', error);
          return interval(refreshInterval);
        }),
      )
      .subscribe();
  }

  stopAutoRefresh(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
      this.refreshSubscription = null;
    }
  }

  refreshToken(): Observable<JwtAuthResponse> {
    const config = this.authConfig.getConfig();
    const refreshToken = this.tokenStorage.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No hay refresh token disponible');
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.http
      .post<JwtAuthResponse>(`${config.apiUrl}${config.refreshTokenEndpoint}`, {
        refresh: refreshToken,
      })
      .pipe(
        retry({
          count: this.MAX_RETRY_ATTEMPTS,
          delay: (error, retryCount) => {
            console.error('Error:', error);
            console.warn(`Reintentando... intento ${retryCount}`);
            return timer(retryCount * 1000);
          },
        }),
        tap((response) => {
          this.isRefreshing = false;

          this.tokenStorage.setToken(response.access);

          if (response.refresh) {
            this.tokenStorage.setRefreshToken(response.refresh);
          }

          this.refreshTokenSubject.next(response.access);
        }),
        catchError((error) => {
          this.isRefreshing = false;
          return throwError(() => error);
        }),
      );
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }
}
